import * as XLSX from "xlsx";
import JSZip from "jszip";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  getInvoiceNo,
  orderDiscount,
  orderExtraCharge,
  calculateItemTax,
  orderSubtotal,
  isWestBengalState
} from "./orderHelpers";

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/**
 * Filter orders by month and year based on their createdAt date.
 */
export const filterOrdersByMonthYear = (orders = [], monthIndex, year) => {
  return orders.filter((o) => {
    const raw = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : o.createdAt;
    if (!raw) return false;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return false;
    return d.getMonth() === Number(monthIndex) && d.getFullYear() === Number(year);
  });
};

/**
 * Calculate full tax and shipping breakdown for a single order.
 *   - If Non-GST order: tax is 0, CGST/SGST/IGST are 0.
 *   - If State is West Bengal: Intra-State -> CGST 50% + SGST 50%
 *   - If State is outside West Bengal: Inter-State -> IGST 100%
 */
export const getOrderTaxBreakdown = (order, products = [], company) => {
  const isDeliveryIncluded = order.deliveryIncluded !== false;

  // Shipping
  const productShippingTotal = (order.items || []).reduce((sum, it) => {
    const prod = products.find((p) => p.id === it.id);
    const shipUnit =
      it.shippingCharge != null && it.shippingCharge !== ""
        ? Number(it.shippingCharge)
        : Number(prod?.shippingCharge || 0);
    return sum + shipUnit * Number(it.quantity || 1);
  }, 0);

  const unincludedShipping =
    Number(order.shippingCharge || 0) > 0
      ? Number(order.shippingCharge)
      : productShippingTotal;
  const invoiceShipping = isDeliveryIncluded ? 0 : unincludedShipping;

  const items = order.items || [];
  const itemsSubtotal = items.reduce(
    (s, it) => s + Number(it.price || 0) * Number(it.quantity || 1),
    0
  );
  const discount = orderDiscount(order);
  const extraCharge = orderExtraCharge(order);
  const grandTotal = Math.max(0, itemsSubtotal + invoiceShipping + extraCharge - discount);

  // Check if this is a Non-GST order
  const isNonGstOrder =
    order.isNonGst === true ||
    order.nonGst === true ||
    order.isGstOrder === false ||
    order.gstType === "non-gst" ||
    order.gstMode === "none" ||
    order.gstMode === "non-gst";

  if (isNonGstOrder) {
    return {
      itemsWithTax: items.map((it) => ({
        ...it,
        rate: 0,
        tax: {
          exclTax: Number(it.price || 0) * Number(it.quantity || 1),
          taxAmount: 0,
          totalInclTax: Number(it.price || 0) * Number(it.quantity || 1),
        },
      })),
      taxableValue: itemsSubtotal,
      totalTaxAmount: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      primaryGstRate: 0,
      itemsSubtotal,
      invoiceShipping,
      discount,
      extraCharge,
      grandTotal,
      isIntraState: false,
      isNonGst: true,
    };
  }

  // Items with tax calculation
  const itemsWithTax = items.map((it) => {
    let rate =
      it.taxRate != null && it.taxRate !== ""
        ? Number(it.taxRate)
        : it.gstRate != null
        ? Number(it.gstRate)
        : order.gstRate != null
        ? Number(order.gstRate)
        : 0;

    if (rate === 0) {
      const prod = products.find((p) => p.id === it.id || (p.sku && p.sku === it.sku));
      if (prod?.gstRate != null && Number(prod.gstRate) > 0) {
        rate = Number(prod.gstRate);
      } else {
        rate = 18;
      }
    }

    const tax = calculateItemTax(it.price, it.quantity, rate);
    return { ...it, tax, rate };
  });

  const taxableValue = itemsWithTax.reduce((s, it) => s + it.tax.exclTax, 0);
  const totalTaxAmount = itemsWithTax.reduce((s, it) => s + it.tax.taxAmount, 0);

  // State matching for GST: Intra-State (West Bengal) vs Inter-State (Outside WB)
  const isIntraState = isWestBengalState(order.state || "West Bengal");

  let cgst = 0;
  let sgst = 0;
  let igst = 0;

  if (totalTaxAmount > 0) {
    if (isIntraState) {
      cgst = Number((totalTaxAmount / 2).toFixed(2));
      sgst = Number((totalTaxAmount - cgst).toFixed(2));
    } else {
      igst = totalTaxAmount;
    }
  }

  const primaryGstRate =
    itemsWithTax.length > 0 ? itemsWithTax[0].rate : 0;

  return {
    itemsWithTax,
    taxableValue,
    totalTaxAmount,
    cgst,
    sgst,
    igst,
    primaryGstRate,
    itemsSubtotal,
    invoiceShipping,
    discount,
    extraCharge,
    grandTotal,
    isIntraState,
    isNonGst: false,
  };
};

/**
 * Generate Excel file (.xlsx) with Monthly Sales Register, GST Rate Summary, and HSN Summary.
 * Excludes Non-GST orders from GST register.
 */
export const exportGstSalesExcel = (
  orders = [],
  products = [],
  monthIndex,
  year,
  company
) => {
  const monthName = MONTH_NAMES[monthIndex];
  const hasGST = Boolean(company?.gst && company.gst.trim());

  // Exclude Non-GST orders from GST register
  const gstOrders = orders.filter((o) => !(
    o.isNonGst === true ||
    o.nonGst === true ||
    o.isGstOrder === false ||
    o.gstType === "non-gst" ||
    o.gstMode === "none" ||
    o.gstMode === "non-gst"
  ));

  // 1. Prepare Sales Register rows
  const registerRows = [];
  let sumQty = 0;
  let sumGross = 0;
  let sumDiscount = 0;
  let sumShipping = 0;
  let sumExtra = 0;
  let sumTaxable = 0;
  let sumCGST = 0;
  let sumSGST = 0;
  let sumIGST = 0;
  let sumTax = 0;
  let sumGrand = 0;

  const rateAggregation = {};
  const hsnAggregation = {};

  gstOrders.forEach((o) => {
    const rawDate = o.createdAt?.seconds ? o.createdAt.seconds * 1000 : o.createdAt;
    const dateObj = rawDate ? new Date(rawDate) : new Date();
    const dateStr = !isNaN(dateObj.getTime())
      ? dateObj.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" })
      : "";

    const invoiceNo = getInvoiceNo(o);
    const breakdown = getOrderTaxBreakdown(o, products, company);

    const itemsSummary = (o.items || [])
      .map((it) => `${it.quantity || 1}x ${it.name}${it.variantTag && it.variantTag !== "Default" ? ` (${it.variantTag})` : ""}`)
      .join("; ");

    const hsnCodes = Array.from(
      new Set(
        (o.items || [])
          .map((it) => it.hsn || it.hsnCode || "4202")
          .filter(Boolean)
      )
    ).join(", ") || "4202";

    const totalQty = (o.items || []).reduce((s, it) => s + Number(it.quantity || 1), 0);

    sumQty += totalQty;
    sumGross += breakdown.itemsSubtotal;
    sumDiscount += breakdown.discount;
    sumShipping += breakdown.invoiceShipping;
    sumExtra += breakdown.extraCharge;
    sumTaxable += breakdown.taxableValue;
    sumCGST += breakdown.cgst;
    sumSGST += breakdown.sgst;
    sumIGST += breakdown.igst;
    sumTax += breakdown.totalTaxAmount;
    sumGrand += breakdown.grandTotal;

    // Aggregate by rate & HSN
    (breakdown.itemsWithTax || []).forEach((it) => {
      const r = it.rate || 0;
      const hsn = it.hsn || it.hsnCode || "4202";

      // Rate aggregation
      if (!rateAggregation[r]) {
        rateAggregation[r] = {
          rate: r,
          taxable: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          tax: 0,
          total: 0,
        };
      }
      rateAggregation[r].taxable += it.tax.exclTax;
      rateAggregation[r].tax += it.tax.taxAmount;
      rateAggregation[r].total += it.tax.totalInclTax;

      // HSN aggregation
      if (!hsnAggregation[hsn]) {
        hsnAggregation[hsn] = {
          hsn,
          name: it.name || "Leather Bags",
          qty: 0,
          taxable: 0,
          cgst: 0,
          sgst: 0,
          igst: 0,
          totalTax: 0,
          totalVal: 0,
        };
      }
      hsnAggregation[hsn].qty += Number(it.quantity || 1);
      hsnAggregation[hsn].taxable += it.tax.exclTax;
      hsnAggregation[hsn].totalTax += it.tax.taxAmount;
      hsnAggregation[hsn].totalVal += it.tax.totalInclTax;

      if (breakdown.isIntraState) {
        const half = Number((it.tax.taxAmount / 2).toFixed(2));
        const otherHalf = Number((it.tax.taxAmount - half).toFixed(2));
        rateAggregation[r].cgst += half;
        rateAggregation[r].sgst += otherHalf;
        hsnAggregation[hsn].cgst += half;
        hsnAggregation[hsn].sgst += otherHalf;
      } else {
        rateAggregation[r].igst += it.tax.taxAmount;
        hsnAggregation[hsn].igst += it.tax.taxAmount;
      }
    });

    registerRows.push({
      "Invoice No": invoiceNo,
      "Date": dateStr,
      "Order ID": o.id || "",
      "Customer Name": o.customerName || "",
      "Customer GSTIN": o.customerGst || o.gstin || "—",
      "Phone": o.phone || "",
      "State (Place of Supply)": o.state || "West Bengal",
      "Tax Region": breakdown.isIntraState ? "Intra-State (WB: CGST+SGST)" : "Inter-State (IGST)",
      "City": o.city || "",
      "PIN Code": o.pincode || "",
      "HSN/SAC Code(s)": hsnCodes,
      "Items Description": itemsSummary,
      "Qty (Pcs)": totalQty,
      "Gross Subtotal (₹)": Number(breakdown.itemsSubtotal.toFixed(2)),
      "Discount (₹)": Number(breakdown.discount.toFixed(2)),
      "Shipping Charge (₹)": Number(breakdown.invoiceShipping.toFixed(2)),
      "Extra Charge (₹)": Number(breakdown.extraCharge.toFixed(2)),
      ...(hasGST
        ? {
            "Taxable Value (₹)": Number(breakdown.taxableValue.toFixed(2)),
            "GST Rate (%)": `${breakdown.primaryGstRate}%`,
            "CGST (₹)": Number(breakdown.cgst.toFixed(2)),
            "SGST (₹)": Number(breakdown.sgst.toFixed(2)),
            "IGST (₹)": Number(breakdown.igst.toFixed(2)),
            "Total GST Amount (₹)": Number(breakdown.totalTaxAmount.toFixed(2)),
          }
        : {}),
      "Invoice Grand Total (₹)": Number(breakdown.grandTotal.toFixed(2)),
      "Payment Mode": o.orderMode === "Offline" ? "Store / UPI" : "Online / WhatsApp",
      "Carrier Partner": o.courierName || "Others",
      "Tracking / Consignment No": o.consignmentNumber || o.trackingNumber || "—",
      "Status": (o.status || "Completed").toUpperCase(),
    });
  });

  // Summary Row
  registerRows.push({
    "Invoice No": "TOTALS",
    "Date": `Month: ${monthName} ${year}`,
    "Order ID": `Total GST Orders: ${gstOrders.length}`,
    "Customer Name": "",
    "Customer GSTIN": "",
    "Phone": "",
    "State (Place of Supply)": "",
    "Tax Region": "",
    "City": "",
    "PIN Code": "",
    "HSN/SAC Code(s)": "",
    "Items Description": "",
    "Qty (Pcs)": sumQty,
    "Gross Subtotal (₹)": Number(sumGross.toFixed(2)),
    "Discount (₹)": Number(sumDiscount.toFixed(2)),
    "Shipping Charge (₹)": Number(sumShipping.toFixed(2)),
    "Extra Charge (₹)": Number(sumExtra.toFixed(2)),
    ...(hasGST
      ? {
          "Taxable Value (₹)": Number(sumTaxable.toFixed(2)),
          "GST Rate (%)": "—",
          "CGST (₹)": Number(sumCGST.toFixed(2)),
          "SGST (₹)": Number(sumSGST.toFixed(2)),
          "IGST (₹)": Number(sumIGST.toFixed(2)),
          "Total GST Amount (₹)": Number(sumTax.toFixed(2)),
        }
      : {}),
    "Invoice Grand Total (₹)": Number(sumGrand.toFixed(2)),
    "Payment Mode": "",
    "Carrier Partner": "",
    "Tracking / Consignment No": "",
    "Status": "",
  });

  // 2. Prepare GST Rate Summary rows
  const rateSummaryRows = Object.values(rateAggregation).map((entry) => ({
    "GST Rate (%)": `${entry.rate}%`,
    "Taxable Value (₹)": Number(entry.taxable.toFixed(2)),
    "CGST (₹)": Number(entry.cgst.toFixed(2)),
    "SGST (₹)": Number(entry.sgst.toFixed(2)),
    "IGST (₹)": Number(entry.igst.toFixed(2)),
    "Total Tax Amount (₹)": Number(entry.tax.toFixed(2)),
    "Total Value (₹)": Number(entry.total.toFixed(2)),
  }));

  // 3. Prepare HSN Summary rows (GSTR-1 Table 12 format)
  const hsnSummaryRows = Object.values(hsnAggregation).map((entry) => ({
    "HSN/SAC Code": entry.hsn,
    "Description": entry.name,
    "UQC": "PCS",
    "Total Quantity": entry.qty,
    "Total Value (₹)": Number(entry.totalVal.toFixed(2)),
    "Taxable Value (₹)": Number(entry.taxable.toFixed(2)),
    "Central Tax (CGST) (₹)": Number(entry.cgst.toFixed(2)),
    "State Tax (SGST) (₹)": Number(entry.sgst.toFixed(2)),
    "Integrated Tax (IGST) (₹)": Number(entry.igst.toFixed(2)),
    "Total Tax Amount (₹)": Number(entry.totalTax.toFixed(2)),
  }));

  // Create Workbook
  const wb = XLSX.utils.book_new();

  const wsSales = XLSX.utils.json_to_sheet(registerRows);

  // Set nice column widths
  const salesColWidths = [
    { wch: 22 }, // Invoice No
    { wch: 12 }, // Date
    { wch: 18 }, // Order ID
    { wch: 20 }, // Customer Name
    { wch: 18 }, // Customer GSTIN
    { wch: 14 }, // Phone
    { wch: 18 }, // State
    { wch: 26 }, // Tax Region
    { wch: 16 }, // City
    { wch: 10 }, // PIN
    { wch: 16 }, // HSN/SAC
    { wch: 35 }, // Items Description
    { wch: 10 }, // Qty
    { wch: 18 }, // Gross Subtotal
    { wch: 12 }, // Discount
    { wch: 18 }, // Shipping
    { wch: 14 }, // Extra Charge
  ];
  if (hasGST) {
    salesColWidths.push(
      { wch: 16 }, // Taxable Value
      { wch: 12 }, // Rate
      { wch: 12 }, // CGST
      { wch: 12 }, // SGST
      { wch: 12 }, // IGST
      { wch: 18 }  // Total GST
    );
  }
  salesColWidths.push(
    { wch: 20 }, // Grand Total
    { wch: 16 }, // Payment
    { wch: 16 }, // Carrier
    { wch: 24 }, // Tracking
    { wch: 14 }  // Status
  );
  wsSales["!cols"] = salesColWidths;

  XLSX.utils.book_append_sheet(wb, wsSales, "Sales Register");

  if (hasGST && rateSummaryRows.length > 0) {
    const wsRates = XLSX.utils.json_to_sheet(rateSummaryRows);
    wsRates["!cols"] = [
      { wch: 14 },
      { wch: 18 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
      { wch: 20 },
      { wch: 20 },
    ];
    XLSX.utils.book_append_sheet(wb, wsRates, "GST Rate Summary");
  }

  if (hasGST && hsnSummaryRows.length > 0) {
    const wsHsn = XLSX.utils.json_to_sheet(hsnSummaryRows);
    wsHsn["!cols"] = [
      { wch: 16 },
      { wch: 24 },
      { wch: 10 },
      { wch: 14 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 20 },
      { wch: 20 },
    ];
    XLSX.utils.book_append_sheet(wb, wsHsn, "HSN Summary");
  }

  const fileName = `GST_Sales_Report_${monthName}_${year}.xlsx`;
  XLSX.writeFile(wb, fileName);
  return fileName;
};

/**
 * Capture an invoice DOM element to high-res PDF blob using html2canvas and jsPDF.
 */
export const captureInvoiceElementToPdfBlob = async (element) => {
  if (!element) throw new Error("Invoice element not found");

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: "#ffffff",
    logging: false,
    windowWidth: 794, // Standard 210mm @ 96 DPI
  });

  const imgData = canvas.toDataURL("image/jpeg", 0.95);
  const pdf = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
    compress: true,
  });

  pdf.addImage(imgData, "JPEG", 0, 0, 210, 297);
  return pdf.output("blob");
};
